import { toMinutes, useApi } from "@platform/api";
import { logger } from "@platform/browser-logger";
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { AxiosInstance } from "axios";

/**
 * Brokerage address from API
 */
export interface BrokerageAddress {
  id: number;
  address: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
}

/**
 * Producer/submitter info
 */
export interface BrokerageContact {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

/**
 * Response from GET /brokerportal/v1/rfp/{rfpId}/brokerage
 */
export interface RfpBrokerageResponse {
  id: number | null;
  name: string | null;
  type: string | null;
  variant: string | null;
  address: BrokerageAddress | null;
  submitter: BrokerageContact;
  clientInfo: {
    clientExecutive: string | null;
    producer: BrokerageContact | null;
    practice: { name: string | null; value: string | null } | null;
    corporateEntity: { name: string | null; value: string | null } | null;
    officeLocation: { name: string | null; value: string | null } | null;
    onedigitalOffice: string | null;
    onedigitalOfficeOptions: string[] | null;
    config: {
      id: number;
      clientExecutive: boolean;
      practice: boolean;
      corporateEntity: boolean;
      officeLocation: boolean;
      centro_select_carrier_enabled: boolean;
      onedigitalOffice: boolean;
    };
  };
}

/**
 * Fetches brokerage details for an RFP from BrokerPortal
 *
 * Uses BrokerPortal endpoint: GET /brokerportal/v1/rfp/{rfpId}/brokerage
 *
 * @param rfpId - The broker portal RFP ID
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to brokerage details response
 *
 * @example
 * ```ts
 * const response = await getRfpBrokerage("123", axiosInstance);
 * const brokerageDetails = transformBrokerageResponse(response);
 * ```
 */
export const getRfpBrokerage = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<RfpBrokerageResponse> => {
  logger.debug("[SendRFP API] Fetching RFP brokerage details:", { rfpId });

  const response = await axiosInstance.get<RfpBrokerageResponse>(
    `brokerportal/v1/rfp/${rfpId}/brokerage`,
  );

  logger.debug("[SendRFP API] Fetched RFP brokerage details:", {
    rfpId,
    brokerageName: response.data.name,
  });

  return response.data;
};

export const rfpBrokerageQueryKey = (rfpId: string) =>
  ["rfp", rfpId, "brokerage"] as const;

type UseRfpBrokerageOptions<TSelected> = {
  rfpId: string;
} & Omit<
  UseQueryOptions<RfpBrokerageResponse, Error, TSelected>,
  "queryKey" | "queryFn"
>;

/**
 * Hook to fetch brokerage details for an RFP
 *
 * Generic over `TSelected` — pass a `select` function to transform the raw
 * `RfpBrokerageResponse` into any shape. When omitted, the raw response is returned.
 *
 * All standard `useQuery` options (e.g. `staleTime`, `retry`, `select`) are forwarded.
 *
 * @param options.rfpId - The broker portal RFP ID
 * @param options.enabled - Whether the query should execute (default: true)
 * @param options.select - Optional transform applied on cache read
 */
export function useRfpBrokerage<TSelected = RfpBrokerageResponse>({
  rfpId,
  enabled = true,
  staleTime = toMinutes(5),
  ...options
}: UseRfpBrokerageOptions<TSelected>): UseQueryResult<TSelected, Error> {
  const axiosInstance = useApi();

  return useQuery<RfpBrokerageResponse, Error, TSelected>({
    queryKey: rfpBrokerageQueryKey(rfpId),
    queryFn: () => getRfpBrokerage(rfpId, axiosInstance),
    enabled: !!rfpId && enabled,
    staleTime,
    ...options,
  });
}
